import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Box, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp, doc, setDoc } from 'firebase/firestore';

const generateOrderId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { clearCart } = useCart();
  
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [displayOrderId, setDisplayOrderId] = useState<string>('');
  const [displayDeliveryDate, setDisplayDeliveryDate] = useState<string>('');

  useEffect(() => {
    // Caso 1: Navegación directa con estado (desde Checkout sin redirección)
    if (location.state?.orderId) {
      setDisplayOrderId(location.state.orderId);
      setDisplayDeliveryDate(location.state.deliveryDate || '');
      setLoading(false);
      return;
    }

    // Caso 2: Procesar redirección desde Stripe
    const processStripeRedirect = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const clientSecret = params.get('payment_intent_client_secret');
        const pendingStr = localStorage.getItem('pendingOrder');

        // Si no es un retorno de Stripe, terminamos carga
        if (!clientSecret) {
          setLoading(false);
          return;
        }

        // Si ya guardamos o no hay orden pendiente, terminamos
        if (saved || !pendingStr) {
          setLoading(false);
          return;
        }

        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');
        if (!stripe) {
          setLoading(false);
          return;
        }

        const pi = await stripe.retrievePaymentIntent(clientSecret);
        if (pi?.paymentIntent?.status === 'succeeded') {
          const data = JSON.parse(pendingStr);
          
          if (db && user?.id) {
            const newOrderId = generateOrderId();
            const payload = {
              id: newOrderId,
              ...data,
              status: 'paid',
              createdAt: serverTimestamp(),
            };
            
            await setDoc(doc(db, 'users', user.id, 'orders', newOrderId), payload);
            setDisplayOrderId(newOrderId);
            // La fecha viene dentro de shipping
            setDisplayDeliveryDate(data.shipping?.deliveryDate || '');
            setSaved(true);

            try { localStorage.removeItem('pendingOrder'); } catch {}
            try { clearCart(); } catch {}
          }
        }
      } catch (err) {
        console.warn('Error procesando redirección Stripe', err);
      } finally {
        setLoading(false);
      }
    };

    processStripeRedirect();
  }, [user?.id, saved, clearCart, location.state]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Confirmando tu pedido...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 4, md: 6 }, 
          textAlign: 'center',
          borderRadius: 4,
          boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
          backgroundColor: '#fff'
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 3 }} />
        
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#333', mb: 2 }}>
          ¡Pedido Realizado con Éxito!
        </Typography>
        
        <Typography variant="body1" sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}>
          Gracias por tu compra. Hemos recibido tu pedido y lo estamos procesando.
          Recibirás un correo de confirmación con los detalles de tu pedido.
        </Typography>

        <Box sx={{ mb: 5, p: 3, bgcolor: '#f9f9f9', borderRadius: 3, border: '1px solid #eee' }}>
          <Typography variant="body1" sx={{ color: '#333', mb: 1, fontWeight: 'bold' }}>
            Número de Pedido: <span style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>{displayOrderId || 'Pendiente'}</span>
          </Typography>
          <Typography variant="body1" sx={{ color: '#333' }}>
            Entrega estimada: <strong>{displayDeliveryDate || 'Fecha programada'}</strong>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="contained"
            onClick={() => navigate('/catalog')}
            size="large"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
            }}
          >
            Seguir Comprando
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/profile', { state: { tab: 1 } })}
            size="large"
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4
            }}
          >
            Ver Mis Pedidos
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderSuccessPage;
